/**
 * Bank-overlay marks: which of a lift's Elevator Control states another bank
 * drew on it.
 *
 * A bank overlay has VTS re-publish a source bank's Control messages on a
 * target bank's lifts, stamped with `overlay_source_bank_id` — a hidden
 * discrete-input module's secured floors drawn on the elevator bank's cars,
 * say. The state itself is applied exactly as if the target had reported it;
 * these marks only remember where it came from, so a floor cell can name the
 * source and grey the state out while the source bank is offline.
 *
 * Every function here is pure and returns its input array unchanged when there
 * is nothing to do, so the reducer's "nothing changed, keep the same state
 * reference" check keeps working.
 */
import { OverlaidMark } from "../../interfaces/Monitoring.interface";
import { BankKey } from "../../utils/typedKeys";
import { Control } from "../generated/control/control";

/**
 * Record or clear the provenance of one Elevator Control state after a frame
 * set it.
 *
 * - A copy (`sourceBankId` set) that turns the state on records a mark naming
 *   its source; one that turns it off removes the mark, because the state is
 *   gone.
 * - The lift's own report of the same state (`sourceBankId` null) removes the
 *   mark either way: the state is now the lift's own, whatever it says.
 */
export function updateOverlaidMarks(
  marks: OverlaidMark[],
  control: Control,
  position: number,
  active: boolean,
  sourceBankId: BankKey | null,
): OverlaidMark[] {
  const index = marks.findIndex((mark) => mark.control === control && mark.position === position);
  const wanted: OverlaidMark | null =
    sourceBankId !== null && active ? { control, position, sourceBankId } : null;
  if (index === -1) return wanted === null ? marks : [...marks, wanted];
  if (wanted !== null && marks[index].sourceBankId === wanted.sourceBankId) return marks;
  const rest = marks.filter((_, i) => i !== index);
  return wanted === null ? rest : [...rest, wanted];
}

/** The mark for one Elevator Control state on one floor, or null when the lift reported it itself. */
export function overlaidMarkAt(
  marks: readonly OverlaidMark[] | undefined,
  control: Control,
  position: number,
): OverlaidMark | null {
  return (
    (marks ?? []).find((mark) => mark.control === control && mark.position === position) ?? null
  );
}
